import { isCallingEnabled } from "../../../utils/orgFeatures";
import { getTelephonyErrorMessage } from "./telephonyErrorMessages";
import { placeBrowserCall } from "./placeBrowserCall";

const PHONE_FIELD_KEYS = ["phoneNumber", "mobile", "mobileNo", "phone", "mobileNumber"];

/** Phone fields may hold several comma-separated numbers; dial the first valid one. */
const firstPhoneNumber = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized === "—" || normalized === "-") {
    return "";
  }
  for (const part of normalized.split(",")) {
    const candidate = part.trim();
    if (candidate && candidate !== "—" && candidate !== "-") {
      return candidate;
    }
  }
  return "";
};

export const resolveTicketPhoneNumber = (ticket, getFieldValue) => {
  if (!ticket) return "";

  for (const key of PHONE_FIELD_KEYS) {
    const value = firstPhoneNumber(getFieldValue(ticket, key, ""));
    if (value) {
      return value;
    }
  }

  const customFields = ticket.customFields || {};
  for (const [key, value] of Object.entries(customFields)) {
    const normalizedKey = String(key || "").toLowerCase();
    if (!normalizedKey.includes("phone") && !normalizedKey.includes("mobile")) {
      continue;
    }
    const normalizedValue = firstPhoneNumber(value);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
};

/**
 * Creates a manual call for a ticket account and dials via browser phone when enabled.
 */
export const initiateTicketBrowserCall = async ({
  ticket,
  getFieldValue,
  createManual,
  browserPhoneStore,
  browserPhoneActions,
  toast,
  t,
}) => {
  try {
    if (!isCallingEnabled()) {
      toast.error(t("ccCallingDisabled"));
      return null;
    }

    const accountNo = String(ticket?.accountNo ?? ticket?.accountId ?? "").trim();
    if (!accountNo) {
      toast.error(t("ccAccountNumberRequired"));
      return null;
    }

    const phoneNumber = resolveTicketPhoneNumber(ticket, getFieldValue);
    const payload = {
      accountNo,
      ticketId: ticket.id,
    };
    if (phoneNumber) {
      payload.phoneNumber = phoneNumber;
    }

    const callResponse = await createManual(payload);
    const browserEnabled = Boolean(browserPhoneStore?.config?.enableBrowserPhone);
    toast.success(t("ccToastCallInitiated"));

    const dialNumber = String(callResponse?.phoneNumber || phoneNumber || "").trim();
    if (!dialNumber || !browserEnabled) {
      return callResponse;
    }

    await placeBrowserCall({
      actions: browserPhoneActions,
      phoneNumber: dialNumber,
      options: {
        callLogId: callResponse?.id,
        accountNo,
      },
      toast,
      t,
    });

    return callResponse;
  } catch (error) {
    toast.error(getTelephonyErrorMessage(error, t));
    return null;
  }
};
